// src/components/Annotation/VehicleSegmentMode.ts
import { ObbAnnotation } from "../../api/Application";
import { DrawingStateFns, Mode, ModeVehicleParts } from "./ModeContext";
import { parseVehicleType } from "./VehicleType";
import { buildGeoJsonLayer, buildPartsFeatures, FeatureCol, hasConfigs, isSupported, isValid, projectToVehicleLength } from "./VehiclePartsModeUtil";

let partLayerCache: { key: string; layer: FeatureCol } | null = null;

export function createPartMode(): ModeVehicleParts {
    return { mode: Mode.VehicleParts, annotation: undefined, points: [], review: false };
}

export const vehicleSegmentModeFns: DrawingStateFns = {
    getCommandSlots( drawMode ) {
        const mode = drawMode as ModeVehicleParts;
        return [
            { key: "q", label: "Undo",   color: "white", active: false },
            { key: "w", label: "Reset",  color: "white", active: false },
            null, 
            null,
            { key: "t", label: "Review", color: "white", active: false },
            null, null, null, null, null,
            { key: "z", label: "Undo",   color: "white", active: false },
            // { key: "Enter", label: `Save (${m.points.length}/${required})`, color: "white", active: m.points.length === required && required > 0 },
        ];
    },

    onKeyDown( drawMode, key, shift, ctx ) {
        const mode = drawMode as ModeVehicleParts;
        if( key === "t" ) {
            ctx.setDrawMode( { ...mode, review: !mode.review } );
            return true;
        }
        if( !mode.annotation ) { return false; }
        const ann   = mode.annotation;
        const attrs = mode.annotation.attributes;

        if( key === "q" || key === "z" ) {
            ctx.setDrawMode( { ...mode, points: mode.points.slice( 0, -1 ) } );
            return true;
        }
        if( key === "w" || key === "Delete"  || key === "Backspace" ) {
            ctx.setDrawMode( { ...mode, points: [] } );
            const updated: ObbAnnotation = { ...ann, attributes: { ...attrs, parts: null } };
            ctx.handleEdit( updated ).then( () => {
                ctx.setDrawMode( { ...mode, annotation: updated, points: [] } );
            } );
            return true;
        }
        if( key === "Escape" && mode.annotation != null ) {
            ctx.setDrawMode( { ...mode, annotation: undefined, points: [] } );
            return true;
        }
        if( key === "Enter" ) {
            const sorted = [...mode.points].sort( ( a, b ) => b - a ); //descending = front-to-rear
            if( !isValid( parseVehicleType( ann.attributes.vehicleType! )!, mode.points.length ) ) {
                return false;
            }
            const attrs  = mode.annotation.attributes;
            const updated: ObbAnnotation = { ...mode.annotation, attributes: { ...attrs, parts: sorted } };
            ctx.handleEdit( updated ).then( () => {
                ctx.setDrawMode( { ...mode, annotation: undefined, points: [] } );
            } );
            return true;
        }
        return false;
    },

    onDeckClick( drawMode, info, ctx ) {
        const mode = drawMode as ModeVehicleParts;
        if( !mode.annotation ) { return; }

        const obj = info.object?.properties;
        
        //they clicked off of an object we exit editing
        if( obj === undefined ) {
            ctx.setDrawMode( { ...mode, annotation: undefined, points: [] } );
            return;
        }

        const clicked  = obj;
        const selected = mode.annotation;

        //if they click on a different object we switch to it if it is supported
        //if it is not supported we don't do anything
        if( clicked.obbAnnotationId !== selected.obbAnnotationId ) { 
            if( isSupported( clicked ) ) {            
                ctx.setDrawMode( { ...mode, annotation: { ...obj }, points: [...obj.attributes.parts ?? []] } );
                return;
            }
            ctx.setDrawMode( { ...mode, annotation: undefined, points: [] } );
            return;
        }

        const coord = info.coordinate as [number, number] | undefined;
        if( !coord ) { return; }
        
        const type = parseVehicleType( mode.annotation.attributes?.vehicleType ?? "" );
        if( type === undefined ) { return; }

        if( !hasConfigs( type ) ) { return; }
        let ann = mode.annotation;
        let len = projectToVehicleLength( ann, coord, ctx.imageWidth, ctx.imageHeight );

        //first point on a fresh vehicle: flip OBB if click is on rear half
        if( mode.points.length === 0 ) {
            const existing = ann.attributes?.parts;
            if( !existing && len < 0.5 ) {
                ann = { ...ann, angle: ann.angle + 180 };
                len = 1 - len;
            }
        }
        //enforce front-to-rear: each click must be further from the front
        //(strictly smaller t) than the previous one. reject out-of-order clicks.
        if( mode.points.length > 0 ) {
            const prev = mode.points[mode.points.length - 1];
            if( len >= prev ) { return; }
        }
        ctx.setDrawMode( { ...mode, annotation: ann, points: [...mode.points, len] } );
    },

    onDeckHover( drawMode, info, ctx ) {
        //nothing to do...
    },

    onAnnotationClick( drawMode, ann, ctx ) {
        console.log( "onAnnotationClick" );
        const mode = drawMode as ModeVehicleParts;
        if( !isSupported( ann ) ) { 
            console.log( "onAnnotationClick: Is not supported!" );
            return;
        }
        //seed from existing bodySegments if present so edits start from saved state
        const existing = ann.attributes.parts;
        ctx.setDrawMode( { ...mode, annotation: { ...ann }, points: existing ? [...existing] : [] } );
    },

    filter( drawMode, ann ) {
        //only show annotations that have a vehicle type this mode can handle
        return isSupported( ann );
    },

    getLineColor( drawMode, ann, ctx ) {
        type Color4   = [number, number, number, number];
        const hidden  = [0, 0, 0, 0] as Color4;
        const white   = (alpha: number): Color4 => [ 255, 255, 255,   255 ];
        const red     = (alpha: number): Color4 => [ 255,   0,   0,   255 ];
        const gray    = (alpha: number): Color4 => [ 240, 240, 240, alpha ];
        const yellow  = (alpha: number): Color4 => [ 255, 255,   0, alpha ];
        const green   = (alpha: number): Color4 => [   0, 255,   0, alpha ];

        const getLineColorImpl = (
            edit:    boolean, 
            hover:   boolean, 
            hasType: boolean, 
            hasCfgs: boolean, 
            hasPts:  boolean,
            valid:   boolean,
        ) => { //hasPts means not only does it have points but they are valid for the type                
                const alpha = hover ? 224 : 160;
                //no matter what we hide the lines if it is being edited
                if(  edit                            ) { return          hidden; }
                if(  mode.review && hasPts &&  valid ) { return          hidden; }
                if( !mode.review && hasPts && !valid ) { return    red( alpha ); }
                if( !hasType                         ) { return   gray( alpha ); }
                if( !hasCfgs                         ) { return   gray( alpha ); }
                if( !hasPts                          ) { return yellow( alpha ); }
                return green( alpha );
        }

        //compute all the booleans we
        const mode    = drawMode as ModeVehicleParts;
        const edit    = mode.annotation?.obbAnnotationId === ann.obbAnnotationId;
        const hover   = ann.obbAnnotationId === ctx.hovObj?.obbAnnotationId;
        const typeStr = ann.attributes.vehicleType ?? "";        
        const type    = parseVehicleType( typeStr );
        const hasType = typeStr !== "Unsure" && type !== undefined;
        const hasCfgs = hasType && hasConfigs( type );        

        //if it has type and parts
        const parts   = ann.attributes.parts ?? [];
        const hasPts  = parts.length > 0;
        const valid   = hasType && parts != null && isValid( type, parts.length )
        return getLineColorImpl( edit, hover, hasType, hasCfgs, hasPts, valid );
    },

    getCursor( drawMode ) {
        const mode = drawMode as ModeVehicleParts;        
        return mode.annotation ? "crosshair" : "pointer";
    },

    getAnnotationLabel( drawMode, ann ) {
        const mode     = drawMode as ModeVehicleParts;
        const parts    = ann.attributes.parts;
        const selected = ann.obbAnnotationId === mode.annotation?.obbAnnotationId;
        if( selected || !parts ) { return ""; }
        return String( ann.attributes?.vehicleType?.[0] ?? "" );
    },

    /**
     * Produce a GeoJsonLayer showing the current annotation's body segments as
     * colored rectangles along the vehicle length axis. Called every render.
     * Internally memoized: only rebuilds the layer when its actual inputs change.
     */
    getLayer( drawMode, allAnnotations, ctx ) {
        const mode = drawMode as ModeVehicleParts;
        if( mode.mode !== Mode.VehicleParts ) {
            partLayerCache = null;
            return null;
        }

        //collect every annotation we want to draw slices for:
        //- the currently-edited one (uses mode.points, may be partial/empty)
        //- every other supported annotation that has a valid saved parts array
        const editingId = mode.annotation?.obbAnnotationId;
        const drawList: { ann: ObbAnnotation; pts: number[]; isEditing: boolean }[] = [];
        for( const ann of allAnnotations ) {
            if( !isSupported( ann ) ) { continue; }
            const isEditing = ann.obbAnnotationId === editingId;
            if( isEditing ) {
                drawList.push( { ann: mode.annotation!, pts: mode.points, isEditing: true } );
                continue;
            }
            if( !mode.review ) { 
                continue;
            }
            const saved = ann.attributes.parts;
            if( !saved || saved.length === 0 ) { continue; }
            const type = parseVehicleType( ann.attributes.vehicleType ?? "" );
            if( type === undefined || !isValid( type, saved.length ) ) { continue; }
            drawList.push( { ann, pts: saved, isEditing: false } );
        }

        if( drawList.length === 0 ) {
            partLayerCache = null;
            return null;
        }

        //cache key: every input that affects the rendered geometry
        const keyParts: string[] = [`${ctx.imageWidth}x${ctx.imageHeight}`, `e:${editingId ?? ""}`];
        for( const { ann, pts } of drawList ) {
            keyParts.push(
                `${ann.obbAnnotationId}:${ann.x},${ann.y},${ann.width},${ann.height},${ann.angle}:${pts.join( "," )}`
            );
        }
        const key = keyParts.join( "|" );
        if( partLayerCache && partLayerCache.key === key ) {
            return buildGeoJsonLayer( partLayerCache.layer );
        }
        const layer = buildPartsFeatures( drawList, ctx );
        partLayerCache = { key, layer };
        return buildGeoJsonLayer( layer );
    }
};
